import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import {
  applyFilters,
  getMediaByType,
  sortMedia,
  typeLabel,
  type MediaFilters,
  type SortKey,
} from '@/utils/media';
import FilterBar from '@/pages/category/components/FilterBar';

const PAGE_SIZE = 8;

const emptyFilters: MediaFilters = {
  genres: [],
  regions: [],
  yearFrom: null,
  yearTo: null,
  keyword: '',
};

export default function CategoryFilter() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';

  const [filters, setFilters] = useState<MediaFilters>({ ...emptyFilters, keyword });
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, keyword }));
    setPage(1);
  }, [keyword]);

  const typeItems = useMemo(() => getMediaByType(type), [type]);
  const availableGenres = useMemo(() => {
    const set = new Set<string>();
    typeItems.forEach((item) => item.genres.forEach((g) => set.add(g)));
    return Array.from(set);
  }, [typeItems]);

  const result = useMemo(
    () => sortMedia(applyFilters(typeItems, filters), sort),
    [typeItems, filters, sort]
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const label = typeLabel(type);
  const titleSuffix = keyword ? `搜索“${keyword}”` : `${label}筛选`;
  useSeo({
    title: `${titleSuffix} - 天堂影视`,
    description: `天堂影视${label}高级筛选列表，支持按类型、地区、年份范围与排序多维组合筛选，精准找到你想看的影视内容。`,
  });

  const updateFilters = (next: MediaFilters) => {
    setFilters(next);
    setPage(1);
  };

  const reset = () => {
    setFilters({ ...emptyFilters });
    setSort('updated');
    setPage(1);
  };

  return (
    <div className="container-page py-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={`/category/${type}`}
          className="transition-colors hover:text-primary-600 cursor-pointer"
        >
          {label}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">筛选</span>
      </nav>

      <div className="mt-4 rounded-lg bg-primary-950 px-5 py-6 text-background-50 md:px-7 md:py-8">
        <h1 className="text-2xl font-bold md:text-3xl">
          {keyword ? `搜索：${keyword}` : `${label} · 高级筛选`}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-200/80">
          {keyword
            ? `正在为你搜索与“${keyword}”相关的影视内容，你也可以继续通过下方多维度筛选进一步缩小范围。`
            : '通过类型、地区、年份范围与排序的任意组合，精准定位你想看的影视内容。'}
        </p>
      </div>

      <div className="mt-5">
        <FilterBar
          detailed
          filters={filters}
          sort={sort}
          availableGenres={availableGenres}
          onFiltersChange={updateFilters}
          onSortChange={(s) => {
            setSort(s);
            setPage(1);
          }}
          onReset={reset}
        />
      </div>

      <div className="mt-5 flex items-center justify-between text-sm text-foreground-500">
        <span>
          筛选结果 <strong className="text-primary-600">{result.length}</strong> 部
        </span>
        <Link
          to={`/category/${type}`}
          className="flex items-center gap-1 transition-colors hover:text-accent-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回分类页
        </Link>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {pageItems.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination
            current={current}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      ) : (
        <div className="mt-4">
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无相关内容"
            description={
              keyword
                ? `没有找到与“${keyword}”相关的影视，试试更换关键词或调整筛选条件。`
                : '没有找到符合当前筛选条件的影视，试试调整或重置筛选条件。'
            }
            actionLabel="重置筛选"
            onAction={reset}
          />
        </div>
      )}
    </div>
  );
}